import { motion } from "framer-motion";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";

const STEPS = [
  { id: "scanning_tiktok", label: "TikTok hashtag scan" },
  { id: "scanning_instagram", label: "Instagram trend scan" },
  { id: "scanning_cj", label: "CJ Dropshipping catalog" },
  { id: "analyzing", label: "Claude Sonnet 4.5 analysis" },
];

const ORDER = { pending: 0, scanning_tiktok: 1, scanning_instagram: 2, scanning_cj: 3, analyzing: 4, completed: 5 };

export default function ScanningPanel({ job }) {
  const currentStep = ORDER[job.status] ?? 0;

  return (
    <div className="border border-zinc-200 relative overflow-hidden mb-10" data-testid="scanning-panel">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.08, 0] }}
        transition={{ duration: 2.4, repeat: Infinity }}
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, #002FA7 50%, transparent 100%)",
        }}
      />

      {/* Marquee */}
      <div className="overflow-hidden border-b border-zinc-200 bg-zinc-50 py-2">
        <div className="marquee inline-block">
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="micro-label mx-8 text-zinc-400">
              SCANNING TIKTOK HASHTAGS · EXTRACTING CJ DROPSHIPPING API · CROSS-REFERENCING TREND SIGNALS · CLAUDE SONNET 4.5 ·
            </span>
          ))}
        </div>
      </div>

      <div className="p-8 md:p-10">
        <div className="flex items-center gap-3 mb-8">
          <motion.div
            className="w-3 h-3 klein-bg"
            animate={{ scale: [1, 1.5, 1], opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
          <span className="micro-label text-zinc-900">{job.status_message}</span>
        </div>

        <div className="grid md:grid-cols-4 gap-0 border border-zinc-200">
          {STEPS.map((s, i) => {
            const stepIdx = i + 1;
            const done = currentStep > stepIdx;
            const active = currentStep === stepIdx;
            return (
              <div
                key={s.id}
                className={`p-5 flex flex-col items-start gap-3 ${
                  i < STEPS.length - 1 ? "md:border-r border-zinc-200" : ""
                } ${active ? "bg-zinc-900 text-white" : done ? "bg-white" : "bg-zinc-50"}`}
              >
                <div className="flex items-center justify-between w-full">
                  <span className={`font-mono text-xs ${active ? "text-white/60" : "text-zinc-400"}`}>
                    0{stepIdx}
                  </span>
                  {done ? (
                    <CheckCircle2 size={22} strokeWidth={2.5} className="text-emerald-500" />
                  ) : active ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
                    >
                      <Loader2 size={22} strokeWidth={2.5} />
                    </motion.div>
                  ) : (
                    <Circle size={22} strokeWidth={2.5} className="text-zinc-300" />
                  )}
                </div>
                <div className={`font-bold text-sm leading-snug ${active ? "text-white" : "text-zinc-900"}`}>
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>

        {job.scraped_hashtags?.length > 0 && (
          <div className="mt-8">
            <div className="micro-label mb-3">LIVE SIGNALS · {job.scraped_hashtags.length} captured</div>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-hidden">
              {job.scraped_hashtags.slice(0, 30).map((t) => (
                <motion.span
                  key={t}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-[10px] font-mono uppercase px-2 py-0.5 border border-zinc-300 text-zinc-700"
                >
                  #{t}
                </motion.span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
